/**
 * Iter 36f-1.5 — Tracker marketplace display helpers.
 *
 * Pure functions consumed by `TrackerDashboard.js`. Extracted so the
 * marketplace-display contract can be tested without a DOM and without
 * standing up the whole table component.
 *
 * Contract: every helper reads ONLY from the Tracker target payload
 * already produced by the backend after Iter 36f-1. No backend
 * changes are required for 36f-1.5; this module is a pure presentation
 * layer over `tracker_priority_score`, `recommended_role`,
 * `marketplace_role_explanation`, and the legacy
 * `opportunity_score` / `opportunity_band` fall-throughs.
 */

// Thresholds for the Priority column colour palette. Mirrors the
// existing inverted-opportunity palette already in use in
// TrackerDashboard.js (green = strong, amber = medium, neutral = low).
export const PRIORITY_STRONG_THRESHOLD = 70;
export const PRIORITY_MEDIUM_THRESHOLD = 40;

/**
 * Return the numeric priority value for the Tracker row, or null when
 * the backend hasn't stamped one (older payloads, or non-Tracker
 * surfaces).
 */
export function getPriorityValue(target) {
  if (!target || typeof target !== 'object') return null;
  const v = target.tracker_priority_score;
  return typeof v === 'number' ? v : null;
}

/**
 * Return the badge class name for the Priority chip based on the
 * numeric score. Returns null when there is no score (the cell
 * renders an em-dash placeholder in that case).
 */
export function getPriorityColorClass(score) {
  if (typeof score !== 'number') return null;
  if (score >= PRIORITY_STRONG_THRESHOLD) return 'badge badge-low';   // green
  if (score >= PRIORITY_MEDIUM_THRESHOLD) return 'badge badge-medium';
  return 'badge badge-type';                                          // neutral
}

/**
 * Convert a `marketplace_role_explanation` entry into a stable
 * "platform/adjacent" signal. The classifier emits the exact string
 * "platform/adjacent — fundraising-rail or SaaS brand" for platform
 * brands; we match the prefix so future copy tweaks don't break the
 * chip mapping.
 */
function isPlatformAdjacent(explanation) {
  if (!Array.isArray(explanation)) return false;
  return explanation.some((e) =>
    typeof e === 'string' && e.toLowerCase().includes('platform/adjacent'),
  );
}

/**
 * Compute the Role chip for a target row. Returns null when the row
 * has no `recommended_role` — non-marketplace sectors and rows with
 * no active marketplace alias get no chip at all.
 *
 * Shape: `{ label, className, tooltip, role }`.
 */
export function getRoleChip(target) {
  if (!target || typeof target !== 'object') return null;
  const role = target.recommended_role;
  if (typeof role !== 'string' || !role) return null;

  const explanation = Array.isArray(target.marketplace_role_explanation)
    ? target.marketplace_role_explanation
    : [];

  // Platform / lottery / SaaS brand — neutral muted chip regardless
  // of the classifier's `role` value (today it's always "verify"
  // for platforms, but we anchor on the explanation token).
  if (isPlatformAdjacent(explanation)) {
    return {
      label:     'Platform',
      role:      'platform',
      className: 'badge badge-type',  // muted neutral
      tooltip:   explanation[0] || 'Platform / adjacent — not a direct buyer or supplier',
    };
  }

  const watchouts = Array.isArray(target.marketplace_role_watchouts)
    ? target.marketplace_role_watchouts
    : [];
  const firstExplanation = explanation[0] || watchouts[0] || '';

  switch (role) {
    case 'buyer':
      return {
        label:     'Buyer',
        role:      'buyer',
        className: 'badge badge-low',     // green
        tooltip:   firstExplanation || 'Demand-side opportunity',
      };
    case 'supplier':
      return {
        label:     'Supplier',
        role:      'supplier',
        className: 'badge badge-medium',  // amber — supply-side distinct
        tooltip:   firstExplanation || 'Supply-side opportunity',
      };
    case 'partner':
      return {
        label:     'Partner',
        role:      'partner',
        className: 'badge badge-medium',
        tooltip:   firstExplanation || 'Partner / referral-channel opportunity',
      };
    case 'ecosystem_node':
      return {
        label:     'Ecosystem',
        role:      'ecosystem_node',
        className: 'badge badge-type',    // neutral accent for ecosystem
        tooltip:   firstExplanation || 'Ecosystem partnership body',
      };
    case 'verify':
      return {
        label:     'Verify',
        role:      'verify',
        className: 'badge badge-type',    // neutral
        tooltip:   firstExplanation || 'Verify before outreach',
      };
    default:
      // Unknown role from a forward-compatible backend — surface as
      // a neutral chip with the raw label so the operator notices.
      return {
        label:     String(role),
        role:      String(role),
        className: 'badge badge-type',
        tooltip:   firstExplanation || '',
      };
  }
}

/**
 * Return the compact sub-line text shown under the company name for
 * marketplace-classified rows. Returns null when no marketplace
 * explanation exists — the row falls back to the existing
 * dissolved-state chip and target_url link, unchanged.
 */
export function getRoleSubline(target) {
  if (!target || typeof target !== 'object') return null;
  if (typeof target.recommended_role !== 'string' || !target.recommended_role) {
    return null;
  }
  const explanation = Array.isArray(target.marketplace_role_explanation)
    ? target.marketplace_role_explanation
    : [];
  if (!explanation.length) return null;
  const first = explanation[0];
  return typeof first === 'string' && first.trim() ? first : null;
}

/**
 * Tooltip text for the Priority column header / cell. When the row
 * carries a marketplace classification we surface the role; otherwise
 * we explain the generic composite ranking. Legacy
 * `opportunity_score` / `opportunity_band` is appended as secondary
 * context so power users can still see the Analyse-derived
 * Opportunity value.
 */
export function getPriorityTooltip(target) {
  const score = getPriorityValue(target);
  const role  = target && typeof target.recommended_role === 'string'
    ? target.recommended_role : '';
  const oppScore = target && typeof target.opportunity_score === 'number'
    ? target.opportunity_score : null;
  const oppBand  = target && typeof target.opportunity_band === 'string'
    ? target.opportunity_band : null;

  const parts = [];
  if (score === null) {
    parts.push('Priority unavailable for this row.');
  } else if (role) {
    parts.push(
      `Priority ${score} — role ${role}. ` +
      'Composite ranking includes opportunity, risk, evidence, ' +
      'recency, and (when an active marketplace-app vector is set) ' +
      'a role-aware uplift.'
    );
  } else {
    parts.push(
      `Priority ${score} — composite of opportunity, risk, evidence, recency.`
    );
  }
  if (oppScore !== null) {
    parts.push(`Legacy Opportunity score: ${oppScore}.`);
  } else if (oppBand) {
    parts.push(`Legacy Opportunity band: ${oppBand}.`);
  }
  return parts.join(' ');
}
